import { AnalyticsEvent } from '../../core/AnalyticsEvent'
import { SearchCompletedEventSchema } from '../../schemas/search/SearchCompletedEventSchema'
import { getConfigFor } from '../../../../utils/configService'
import { SPLIT_FEATURES } from '../../../../utils/constants/feature'
import { CommonEventParams, getCommonParams } from '../../utils/CommonParams'
import { getLayoutByName } from '../../../../components/LoadLayout'
import { BUSINESS_VERTICAL } from '../../constants/EventHelper'
import { getPreviousScreenName } from '../../../../lib/analytics/core/EventScreenManager'

const SEARCH_SUGGESTION_COLLECTION = 'SearchSuggestionCollection'

const INIT_USER_SEARCH_COMPLETED_EVENT_PARAMS = {
  SEARCH_FEATURE_SELECTED: null,
  SEARCH_FEATURE_POSITION: -1,
  SEARCH_SUGGESTED_CAMPAIGN_NAME: null,
  SEARCH_SUGGESTED_CAMPAIGN_NAME_COUNT: null,
  SEARCH_POPULAR_CATEGORY_KEYWORDS: null,
  SEARCH_POPULAR_CATEGORY_KEYWORDS_COUNT: 0,
  SEARCH_RECENT_KEYWORDS: null,
  SEARCH_RECENT_KEYWORDS_COUNT: 0,
  SEARCH_REFINEMENT_KEYWORDS: null,
  SEARCH_REFINEMENT_KEYWORDS_COUNT: 0,
  SEARCH_AUTOCOMPLETE_KEYWORDS: null,
  SEARCH_AUTOCOMPLETE_KEYWORDS_COUNT: 0,
  SEARCH_TRENDING_KEYWORDS: null,
  SEARCH_TRENDING_KEYWORDS_COUNT: 0,
  SEARCH_TERM: null,
  SEARCH_TERM_TYPED: null,
  SEARCH_PRODUCT_COUNT: 0,
  SEARCH_SEGMENTS: null
}

export interface UserSearchCompletedEventParams extends CommonEventParams {
  BUSINESS_VERTICAL: string
  EVENT_CATEGORY: string
  SEARCH_FEATURE_SELECTED: string | null
  SEARCH_FEATURE_POSITION: number
  SEARCH_SUGGESTED_CAMPAIGN_NAME: Array<string> | null
  SEARCH_SUGGESTED_CAMPAIGN_NAME_COUNT: number | null
  SEARCH_POPULAR_CATEGORY_KEYWORDS: Array<string> | null
  SEARCH_POPULAR_CATEGORY_KEYWORDS_COUNT: number
  SEARCH_RECENT_KEYWORDS: Array<string> | null
  SEARCH_RECENT_KEYWORDS_COUNT: number
  SEARCH_REFINEMENT_KEYWORDS: Array<string> | null
  SEARCH_REFINEMENT_KEYWORDS_COUNT: number
  SEARCH_AUTOCOMPLETE_KEYWORDS: Array<string> | null
  SEARCH_AUTOCOMPLETE_KEYWORDS_COUNT: number
  SEARCH_TRENDING_KEYWORDS: Array<string> | null
  SEARCH_TRENDING_KEYWORDS_COUNT: number
  SEARCH_TERM: string | null
  SEARCH_TERM_TYPED: string | null
  SEARCH_PRODUCT_COUNT: number
  SEARCH_SEGMENTS: string | null
}

export class SearchCompletedEvent extends AnalyticsEvent {
  constructor(params: UserSearchCompletedEventParams) {
    super(SearchCompletedEventSchema(), ['segment'])
    this.createEventObject(params)
  }
}

export function mergedSearchPropertiesEvent(
  layouts: any,
  productLayout: any,
  searchTerm: string | null = null
): Partial<UserSearchCompletedEventParams> {
  const sessionSearchData = sessionStorage.getItem('searchData')
  let searchStartData = INIT_USER_SEARCH_COMPLETED_EVENT_PARAMS
  if (sessionSearchData) {
    const parsedSearchData = JSON.parse(sessionSearchData)
    searchStartData = parsedSearchData || {}
  }

  const searchSuggestionCollection = getLayoutByName(
    layouts,
    SEARCH_SUGGESTION_COLLECTION
  )

  const productCount = productLayout?.value?.collection?.count || 0
  const searchRefinementKeywords =
    searchSuggestionCollection?.value?.collection?.map?.(
      (item: any) => item.name
    ) ?? []

  const previousScreenName = getPreviousScreenName()
  const searchFeatureSelected = previousScreenName
    ? searchStartData?.SEARCH_FEATURE_SELECTED
    : 'deeplink'

  const userSearchCompletedEventParams = {
    SEARCH_SUGGESTED_CAMPAIGN_NAME: searchStartData?.SEARCH_SUGGESTED_CAMPAIGN_NAME ?? null,
    SEARCH_SUGGESTED_CAMPAIGN_NAME_COUNT: searchStartData?.SEARCH_SUGGESTED_CAMPAIGN_NAME_COUNT ?? 0,
    SEARCH_POPULAR_CATEGORY_KEYWORDS: searchStartData?.SEARCH_POPULAR_CATEGORY_KEYWORDS ?? null,
    SEARCH_POPULAR_CATEGORY_KEYWORDS_COUNT: searchStartData?.SEARCH_POPULAR_CATEGORY_KEYWORDS_COUNT ?? 0,
    SEARCH_RECENT_KEYWORDS: searchStartData?.SEARCH_RECENT_KEYWORDS ?? null,
    SEARCH_RECENT_KEYWORDS_COUNT: searchStartData?.SEARCH_RECENT_KEYWORDS_COUNT ?? 0,
    SEARCH_AUTOCOMPLETE_KEYWORDS: searchStartData?.SEARCH_AUTOCOMPLETE_KEYWORDS ?? null,
    SEARCH_AUTOCOMPLETE_KEYWORDS_COUNT: searchStartData?.SEARCH_AUTOCOMPLETE_KEYWORDS_COUNT ?? 0,
    SEARCH_TRENDING_KEYWORDS: searchStartData?.SEARCH_TRENDING_KEYWORDS ?? null,
    SEARCH_TRENDING_KEYWORDS_COUNT: searchStartData?.SEARCH_TRENDING_KEYWORDS_COUNT ?? 0,
    SEARCH_TERM_TYPED: searchStartData?.SEARCH_TERM_TYPED ?? null,
    SEARCH_SEGMENTS: searchStartData?.SEARCH_SEGMENTS ?? null,
    SEARCH_TERM: searchTerm ?? null,
    SEARCH_REFINEMENT_KEYWORDS: searchRefinementKeywords,
    SEARCH_REFINEMENT_KEYWORDS_COUNT: searchRefinementKeywords.length,
    SEARCH_FEATURE_SELECTED: searchFeatureSelected,
    SEARCH_FEATURE_POSITION: searchFeatureSelected
      ? searchStartData?.SEARCH_FEATURE_POSITION ?? -1
      : -1,
    SEARCH_PRODUCT_COUNT: productCount,
  }
  sessionStorage.removeItem('searchData')
  return userSearchCompletedEventParams
}

export async function trackSearchCompletedEvent(
  searchCompletedParams: UserSearchCompletedEventParams
): Promise<void> {
  searchCompletedParams.BUSINESS_VERTICAL =
    searchCompletedParams.EVENT_CATEGORY = BUSINESS_VERTICAL.GROCERY
  const { isEnabled = false } = getConfigFor(SPLIT_FEATURES.LEAP_CDP_RELEASE_TWO) || {}
  if (isEnabled) {
    const params = {
      ...getCommonParams(),
      ...searchCompletedParams,
    }
    const SEARCH_VIEWALL_ENTER = 'viewall_enter'
    const SEARCH_VIEWALL_TYPE_ENTER = 'viewall_type&enter'
    params.SEARCH_FEATURE_SELECTED =
      searchCompletedParams.SEARCH_FEATURE_SELECTED === SEARCH_VIEWALL_ENTER
        ? SEARCH_VIEWALL_TYPE_ENTER
        : searchCompletedParams.SEARCH_FEATURE_SELECTED
    new SearchCompletedEvent(params).track()
  }
}
